/**
 * User administration and the role vocabulary, behind /configuration.
 *
 * The writes put the username in the body rather than the path: the server's
 * RBAC matrix matches (method, path) literally, so a per-user path could not
 * be granted to a role at all. Deactivate is the exception — a DELETE body is
 * not reliably forwarded, so it goes as a query parameter.
 */
import { api, tokens } from "./api";

/** Assignable roles, each with the sentence describing what it may do. */
export const getRoles = () => api.get("/api/roles");

export const getUsers = () => api.get("/api/users");

export const createUser = (payload) => api.post("/api/users", payload);

/**
 * Patch-style: send only what changed. Omitting `password` keeps the current
 * one — the client never holds it, so a full replace could not round-trip it.
 */
export const updateUser = (payload) => api.put("/api/users", payload);

/** Retires a user. Never deletes the row: audit trails store usernames as
 *  plain strings and would otherwise read as a name with nothing behind it. */
export const deactivateUser = (username) =>
  api.del("/api/users", { params: { username } });

/**
 * Change YOUR OWN password. The server re-verifies the current one, and the
 * change revokes every token issued before it — including the pair this tab
 * is holding, which is why the fresh pair from the response REPLACES the
 * stored one immediately: skip that and the very next request signs the
 * user out of the session they just secured.
 */
export const changeOwnPassword = async (currentPassword, newPassword) => {
  const body = await api.post("/api/users/password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  tokens.set({ access: body.access, refresh: body.refresh });
  return body;
};

/** The editable role → resource CRUD grid. */
export const getAccess = () => api.get("/api/access");
/** Replace one role's grants. Administrator-only, enforced server-side. */
export const saveAccess = (role, grants) =>
  api.put("/api/access", { role, grants });
